// Division abbreviations (Admin → Divisions): the short name the pages show for an A2000 division
// or a Deposco business unit (corp `DEPOSCO`, div its code), the full name on hover. One per corp + div (`division-abbrev`); none saved, or "" (Reset to
// default), is the first 7 characters of the name — a division that appears later has it at once.
// Display only: A2000 data and saved records keep the full name, and search and filters match both.

import type { DeposcoRow, LineSource } from "./deposco";
import { DEPOSCO_CORP, DEPOSCO_DIVISIONS, deposcoDivisionName, deposcoKey } from "./deposco";
import type { OrderRow } from "./pre-route";
import { readBaseline } from "./integrity";
import type { DivisionAbbrevRecord, LinePickTicketRecord, RoutingLineRecord, SourceRow } from "./types";

/** An abbreviation's longest. */
export const ABBREV_MAX = 12;
/** The default: this many characters of the name. */
export const DEFAULT_ABBREV_CHARS = 7;

export const defaultAbbrev = (name: string) => name.trim().slice(0, DEFAULT_ABBREV_CHARS).trim();

/** A division's key: corp + div, as A2000 gives them. */
export const divisionKey = (corp: string, div: string) => `${corp.trim()}|${div.trim()}`;

const nameKey = (name: string) => name.trim().replace(/\s+/g, " ").toUpperCase();

/** One division of a line: its A2000 corp, div and name, and the abbreviation the pages show. */
export interface DivisionRef {
  corp: string;
  div: string;
  name: string;
  abbrev: string;
}

/**
 * A division's abbreviation: the one saved for its corp + div; where only the name is known (a
 * workbook row, a pre-route kept as added), the one saved with that name; else the default.
 */
export function divisionNamer(records: readonly DivisionAbbrevRecord[] = []) {
  const byKey = new Map<string, string>();
  const byName = new Map<string, string>();
  for (const r of records) {
    const a = String(r.abbreviation ?? "").trim();
    if (!a) continue;
    byKey.set(divisionKey(String(r.corp ?? ""), String(r.div ?? "")), a);
    if (r.division && !byName.has(nameKey(r.division))) byName.set(nameKey(r.division), a);
  }
  return (d: { corp?: string; div?: string; name: string }): string => {
    if (!d.name.trim()) return "";
    if (d.corp?.trim() || d.div?.trim())
      return byKey.get(divisionKey(d.corp ?? "", d.div ?? "")) ?? defaultAbbrev(d.name);
    return byName.get(nameKey(d.name)) ?? defaultAbbrev(d.name);
  };
}
export type DivisionNamer = ReturnType<typeof divisionNamer>;

/** A cell's divisions as text: "HANDBAG, SHOES". */
export const divisionAbbrevs = (divs: readonly DivisionRef[]) => divs.map((d) => d.abbrev || d.name);

/**
 * A cell's divisions: each abbreviation and what its hover says — one division, its full name;
 * several, each its code (A2000 DIV, a Deposco business unit), as their full names can be alike.
 */
export const divisionHovers = (divs: readonly DivisionRef[]): { text: string; tip: string }[] =>
  divs.map((d) => ({ text: d.abbrev || d.name, tip: divs.length > 1 ? d.div.trim() || d.name : d.name }));

/**
 * A cell's divisions as shown: one, its abbreviation; several, each abbreviation once with its codes
 * (A2000 DIV, a Deposco business unit) in brackets, sorted — "AXNY (H, TX), EGC (DA)"; a code that is
 * its abbreviation (a Deposco unit, "EGC") not again.
 */
export function divisionLabel(divs: readonly DivisionRef[]): string {
  if (divs.length < 2) return divs[0] ? divs[0].abbrev || divs[0].name : "";
  const codes = new Map<string, Set<string>>();
  for (const d of divs) {
    const text = d.abbrev || d.name;
    if (!text) continue;
    const had = codes.get(text) ?? new Set<string>();
    if (d.div.trim() && d.div.trim() !== text) had.add(d.div.trim());
    codes.set(text, had);
  }
  return [...codes].map(([text, c]) => (c.size ? `${text} (${[...c].sort().join(", ")})` : text)).join(", ");
}

/** A line's division codes when it has more than one: "2 divisions (H, TX)"; else "". */
export function divisionsNote(divs: readonly DivisionRef[]): string {
  const codes = [...new Set(divs.map((d) => d.div.trim()).filter(Boolean))].sort();
  return codes.length > 1 ? `${codes.length} divisions (${codes.join(", ")})` : "";
}

/** A pick ticket's (SO's) division, as its line shows it: "AXNY · H", the full name on hover. */
export function ptDivision(
  divs: readonly DivisionRef[],
  p: { corp: string; div: string; division: string },
): { text: string; tip: string } {
  const ref = divs.find((d) => d.corp === p.corp && d.div === p.div && d.name === p.division);
  const abbrev = ref?.abbrev || defaultAbbrev(p.division) || p.division;
  const code = p.div.trim();
  return { text: code && code !== abbrev ? `${abbrev} · ${code}` : abbrev, tip: p.division };
}

/** The hover of several short names: "text — tip" each (the text alone when they match), never two alike. */
export const hoverRows = (items: readonly { text: string; tip: string }[]) => [
  ...new Set(items.map((i) => (i.tip && i.tip !== i.text ? `${i.text} — ${i.tip}` : i.text))),
];

/** How Ask AI says a division: its abbreviation, the full name in brackets when it differs. */
export const divisionWords = (abbrev: string, name: string) =>
  abbrev && name && abbrev !== name ? `${abbrev} (${name})` : abbrev || name;

/** A line's divisions as Ask AI says them: "HANDBAG (HANDBAGS), SHOES". */
export const divsWords = (divs: readonly DivisionRef[]) => divs.map((d) => divisionWords(d.abbrev, d.name)).join(", ");

/** One division as the Admin page lists it. */
export interface DivisionRow {
  key: string;
  /** A2000, or Deposco (a business unit: corp `DEPOSCO`). */
  source: LineSource;
  corp: string;
  div: string;
  /** The full DIVISION name: the open source's, else the last one seen. */
  name: string;
  /** Open Worklist lines holding a pick ticket of it (a Mixed line counts under each). */
  openLines: number;
  /** What the pages show now. */
  abbrev: string;
  defaultAbbrev: string;
  /** Its record, when one was ever saved ("" abbreviation: back to the default). */
  record: DivisionAbbrevRecord | null;
  /** No abbreviation of its own: the default shows. */
  isDefault: boolean;
  /** Where it was seen: open pick tickets (and SOs), pre-routed or closed orders, finished lines. */
  seenIn: ("open" | "orders" | "history")[];
}

/**
 * Every division the app's data has — the open pick tickets and SOs, the pre-routed orders, the
 * Deposco orders, the lines' pick tickets as approved (History) — and every one with a record, and
 * every Deposco business unit (`DEPOSCO_DIVISIONS`); by name. A workbook row has no corp or div,
 * so it adds none.
 */
export function divisionRows(input: {
  source: readonly SourceRow[];
  lines: readonly RoutingLineRecord[];
  pickTickets: readonly LinePickTicketRecord[];
  orders?: readonly Pick<OrderRow, "corp" | "div" | "division">[];
  /** Every Deposco order (open or closed in the last 30 days). */
  deposco?: readonly Pick<DeposcoRow, "division_code">[];
  records: readonly DivisionAbbrevRecord[];
}): DivisionRow[] {
  const found = new Map<
    string,
    { corp: string; div: string; name: string; seen: Set<DivisionRow["seenIn"][number]> }
  >();
  const see = (corp: unknown, div: unknown, name: unknown, where: DivisionRow["seenIn"][number], wins = false) => {
    const c = String(corp ?? "").trim();
    const d = String(div ?? "").trim();
    if (!c && !d) return;
    const key = divisionKey(c, d);
    const n = String(name ?? "").trim();
    const had = found.get(key);
    if (!had) found.set(key, { corp: c, div: d, name: n, seen: new Set([where]) });
    else {
      had.seen.add(where);
      if (n && (wins || !had.name)) had.name = n;
    }
  };
  // The open source's name wins: what A2000 says now.
  const lineKeys = new Map<string, Set<string>>();
  for (const s of input.source) {
    see(s.corp, s.div, s.division, "open", true);
    const key = divisionKey(String(s.corp ?? ""), String(s.div ?? ""));
    const line = s.deposco
      ? deposcoKey(s)
      : s.in_wms === 1 && s.wms_shipment
        ? `W|${s.wms_shipment}`
        : `P|${s.pick_ticket}`;
    lineKeys.set(key, (lineKeys.get(key) ?? new Set()).add(line));
  }
  for (const o of input.orders ?? []) see(o.corp, o.div, o.division, "orders");
  for (const r of input.deposco ?? [])
    if (r.division_code) see(DEPOSCO_CORP, r.division_code, deposcoDivisionName(r.division_code.trim()), "orders");
  for (const [code, name] of Object.entries(DEPOSCO_DIVISIONS)) see(DEPOSCO_CORP, code, name, "orders");
  for (const l of input.lines) for (const c of readBaseline(l).copies) see(c.corp, c.div, c.division, "history");
  for (const c of input.pickTickets) see(c.corp, c.div, c.division, "history");
  const records = new Map(input.records.map((r) => [divisionKey(String(r.corp ?? ""), String(r.div ?? "")), r]));
  for (const r of records.values())
    if (!found.has(divisionKey(r.corp, r.div))) see(r.corp, r.div, r.division, "history");
  const name = divisionNamer(input.records);
  return [...found.entries()]
    .map(([key, f]) => {
      const record = records.get(key) ?? null;
      const fallback = f.name || record?.division || f.div;
      return {
        key,
        source: f.corp === DEPOSCO_CORP ? ("Deposco" as const) : ("A2000" as const),
        corp: f.corp,
        div: f.div,
        name: fallback,
        openLines: lineKeys.get(key)?.size ?? 0,
        abbrev: name({ corp: f.corp, div: f.div, name: fallback }),
        defaultAbbrev: defaultAbbrev(fallback),
        record,
        isDefault: !record?.abbreviation.trim(),
        seenIn: (["open", "orders", "history"] as const).filter((w) => f.seen.has(w)),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
}

/** What saving an abbreviation would do: refused (empty, too long), or an orange "Are you sure?". */
export type AbbrevCheck =
  | { ok: false; message: string }
  | { ok: true; value: string; warning: { title: string; text: string; lines: string[] } | null };

/**
 * A typed abbreviation: trimmed, not empty, at most 12 characters. Another division showing the
 * same one (any case, its default too) is a warning, not a refusal.
 */
export function checkAbbrev(typed: string, key: string, rows: readonly DivisionRow[]): AbbrevCheck {
  const value = typed.trim().replace(/\s+/g, " ");
  if (!value)
    return {
      ok: false,
      message: "An abbreviation can't be empty. Reset to default shows the first 7 characters of the name.",
    };
  if (value.length > ABBREV_MAX)
    return { ok: false, message: `At most ${ABBREV_MAX} characters — “${value}” has ${value.length}.` };
  const same = rows.filter((r) => r.key !== key && r.abbrev.toUpperCase() === value.toUpperCase());
  return {
    ok: true,
    value,
    warning: same.length
      ? {
          title: "Another division uses it — are you sure?",
          text: `“${value}” is already the abbreviation of ${same.length === 1 ? "another division" : `${same.length} other divisions`}: the pages would show them the same.`,
          lines: same.map((r) => `${r.abbrev} — ${r.name} (${r.corp} ${r.div})`),
        }
      : null,
  };
}

/** Where a division shows its abbreviation: the change entry's line text ("Division T 01 · HANDBAGS"). */
export const divisionText = (r: Pick<DivisionRow, "corp" | "div" | "name">) =>
  `Division ${[r.corp, r.div].filter(Boolean).join(" ")} · ${r.name}`;

/** `GET /api/routing/admin/divisions`: the Admin page's list (the Admin feature only). */
export interface DivisionsPayload {
  divisions: DivisionRow[];
}

/** A line's division chips (its pick tickets' table): each corp + div once, "AXNY · H", the full name on hover. */
export const divisionChips = (divs: readonly DivisionRef[]) => [
  ...new Map(
    divs.map((d) => [
      divisionKey(d.corp, d.div),
      { key: divisionKey(d.corp, d.div), ...ptDivision(divs, { ...d, division: d.name }) },
    ]),
  ).values(),
];

/**
 * The chips a line opens with: those of the divisions the Worklist's Division filter keeps (it keeps
 * full names); none kept, or none of this line's, every one ([]: All).
 */
export const chipsOfFilter = (divs: readonly DivisionRef[], kept: readonly string[]) =>
  kept.length
    ? divs
        .filter((d) => kept.includes(d.name) || kept.includes(divisionFilterValue(d)))
        .map((d) => divisionKey(d.corp, d.div))
    : [];

/**
 * The Division header filter keeps each division apart — two codes of one name too (AXNY FA and H):
 * its value is the name with the corp and code ("AMERICAN EXCHANGE TIME LLC|T|FA"), so Ask AI still
 * reads and sets the name (`divisionFilterName`); a name alone keeps every division of it.
 */
export const divisionFilterValue = (d: Pick<DivisionRef, "name" | "corp" | "div">) =>
  `${d.name}|${divisionKey(d.corp, d.div)}`;
export const divisionFilterName = (value: string) => value.split("|")[0] ?? value;

/**
 * The Division filter as its chip says it: "AXNY · FA" — several of one abbreviation "AXNY · FA, H",
 * of several "AXNY · FA, H; EGC · DA" — never the value it keeps. `divs`: the page's divisions, to
 * find each value's; a name alone (Ask AI) is said as it is.
 */
export function divisionFilterText(values: readonly string[], divs: readonly DivisionRef[]): string {
  const byValue = new Map(divs.map((d) => [divisionFilterValue(d), d]));
  const groups = new Map<string, string[]>();
  for (const v of values) {
    const d = byValue.get(v);
    const head = d ? d.abbrev || d.name : divisionFilterName(v);
    const code = d?.div.trim() && d.div.trim() !== head ? d.div.trim() : "";
    const had = groups.get(head) ?? [];
    if (code && !had.includes(code)) had.push(code);
    groups.set(head, had);
  }
  return [...groups].map(([head, codes]) => (codes.length ? `${head} · ${codes.join(", ")}` : head)).join("; ");
}
/**
 * "AXNY · H — AMERICAN EXCHANGE TIME LLC": how the filter lists it, abbreviation and code first; a
 * code or a name that only repeats the abbreviation is left out (Deposco "AERO").
 */
export function divisionFilterLabel(d: DivisionRef) {
  const short = d.abbrev || d.name;
  const code = d.div.trim();
  const head = code && code !== short ? `${short} · ${code}` : short;
  return d.name && d.name !== short ? `${head} — ${d.name}` : head;
}

/** Whether a pick ticket (SO) is in the chosen chips ([]: All). */
export const inChips = (p: { corp: string; div: string }, chosen: readonly string[]) =>
  !chosen.length || chosen.includes(divisionKey(p.corp, p.div));
